import { Inject, Injectable } from '@angular/core';
import Dexie, { liveQuery } from 'dexie';
import { from, Observable } from 'rxjs';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { EncryptionService } from '@evorait/pace-encryption';
import { ConcurrentUsageDbData, ConcurrentUsageEntryState } from './concurrent-usage.type';
import { ConcurrentStorageData } from '../../types/concurrent-usage';
import { isDefined } from '../../utilities/is-defined';

@Injectable({
    providedIn: 'root',
})
export class ConcurrentUsageDbService extends Dexie {
    public static readonly DB_NAME = 'ConcurrentUsageDatabase';
    public static readonly DB_TABLE = 'concUsageData';
    public static readonly DB_KEY_PATH = 'key';

    private static readonly VERSION_ONE = 1;

    private concUsageData: Dexie.Table<ConcurrentUsageDbData, string>;

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly encryption: EncryptionService,
    ) {
        super(ConcurrentUsageDbService.DB_NAME);
        this.version(ConcurrentUsageDbService.VERSION_ONE).stores({
            [ConcurrentUsageDbService.DB_TABLE]: `${ConcurrentUsageDbService.DB_KEY_PATH},deleted`,
        });
    }

    /**
     * Replaces the local rows of `keys` with `items` in one transaction: every row of `keys` —
     * present or deleted — is dropped, then `items` are written. A key in `keys` without an item
     * ends up absent. Rows of other keys are left untouched, so data that is not part of the device
     * handover (e.g. a `localOnly` persisted slice) survives a check-in.
     *
     * @param keys the keys whose local rows are replaced
     * @param items the new rows, each for a key in `keys`
     */
    public async replaceKeys(keys: readonly string[], items: ConcurrentStorageData[]): Promise<void> {
        const encryptedItems: ConcurrentUsageDbData[] = await Promise.all(
            items.map(async (item) => ({
                key: item.key,
                deleted: ConcurrentUsageEntryState.PRESENT,
                timestamp: item.timestamp ?? Date.now(),
                payload: await this.encryption.encryptAsync(item.value),
            })),
        );

        await this.transaction('rw', this.concUsageData, async () => {
            await this.concUsageData.bulkDelete([...keys]);
            await this.concUsageData.bulkPut(encryptedItems);
        });
    }

    public async getPresentEntries(): Promise<ConcurrentStorageData[]> {
        const allEntries = await this.concUsageData
            .where('deleted')
            .equals(ConcurrentUsageEntryState.PRESENT)
            .toArray();

        return Promise.all(
            allEntries.map(async (entry) => {
                return {
                    key: entry.key,
                    timestamp: entry.timestamp,
                    value: await this.encryption.decryptAsync(entry.payload),
                };
            }),
        );
    }

    public async getDeletedEntries(): Promise<string[]> {
        const allEntries = await this.concUsageData
            .where('deleted')
            .equals(ConcurrentUsageEntryState.DELETED)
            .toArray();
        return allEntries.map((entry) => entry.key);
    }

    public async put(payload: ConcurrentStorageData): Promise<boolean> {
        const encryptedPayload = await this.encryption.encryptAsync(payload.value);

        const data: ConcurrentUsageDbData = {
            key: payload.key,
            deleted: ConcurrentUsageEntryState.PRESENT,
            timestamp: payload.timestamp,
            payload: encryptedPayload,
        };

        await this.concUsageData.put(data);

        return true;
    }

    public async get(key: string): Promise<ConcurrentStorageData | null> {
        const entry = await this.concUsageData.get(key);
        if (!this.isExistingDbEntry(entry)) {
            return null;
        }

        return {
            key: entry.key,
            timestamp: entry.timestamp,
            value: await this.encryption.decryptAsync(entry.payload),
        };
    }

    // TODO: What about the previously thrown errors?
    //       --> replace with logic that creates expected data not on error, but on event with payload 'undefined'
    public liveQuery$(key: string): Observable<ConcurrentStorageData | undefined> {
        return from(
            liveQuery(() =>
                this.concUsageData
                    .where(ConcurrentUsageDbService.DB_KEY_PATH)
                    .equals(key) // key is unique in the db table. this means there is only one entry in the returned array.
                    .first() // first returns the first entry from above array
                    .then((entry) =>
                        !this.isExistingDbEntry(entry)
                            ? undefined
                            : this.encryption.decryptAsync(entry.payload).then((payload) => ({
                                  key: entry.key,
                                  timestamp: entry.timestamp,
                                  value: payload,
                              })),
                    )
                    .catch((error: Error) => {
                        this.log.error(
                            `ConcurrentUsageDbService: liveQuery$: unknown error occurred when reading ${key} from db: ${error}`,
                        );
                        return undefined;
                    }),
            ),
        );
    }

    public async remove(key: string): Promise<boolean> {
        this.log.debug('Marking concurrent usage data for deletion', { key });
        const result = await this.concUsageData.update(key, { deleted: ConcurrentUsageEntryState.DELETED });
        return result > 0;
    }

    /**
     * Drops the row `remove` left behind, once the deletion has reached the backend — the tombstone
     * only exists to remember that the remote delete is still owed (see `getDeletedEntries`), so
     * keeping it after a confirmed delete makes every later `uploadAll` re-send a DELETE for a key
     * that is already gone.
     *
     * Only a row still marked deleted is dropped: a `put` for the same key in the meantime has
     * flipped it back to `PRESENT`, which means the user re-created the entry and its value must
     * survive.
     *
     * @param key the key whose tombstone is no longer needed
     * @returns whether a tombstone was dropped
     */
    public async purge(key: string): Promise<boolean> {
        this.log.debug('Dropping concurrent usage tombstone', { key });
        const purged = await this.concUsageData
            .where(ConcurrentUsageDbService.DB_KEY_PATH)
            .equals(key)
            .and((entry) => entry.deleted === ConcurrentUsageEntryState.DELETED)
            .delete();
        return purged > 0;
    }

    private isExistingDbEntry(entry: ConcurrentUsageDbData | undefined): entry is ConcurrentUsageDbData {
        return isDefined(entry?.payload) && entry.deleted === ConcurrentUsageEntryState.PRESENT;
    }
}
